import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { useSiteCtaStyles } from '../hooks/useSiteCtaStyles';

// ============================================================================
// Slot group definitions — inline literals for the static schema extractor.
// ============================================================================

const image1Def: SlotGroupDefinition = {
  label: 'Pet Image #1',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://images.unsplash.com/photo-1681923216521-8f99c96691dc?auto=format&fit=crop&w=1000&q=80',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Resident holding her cat close to her face',
    },
  },
};

const image2Def: SlotGroupDefinition = {
  label: 'Pet Image #2',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://images.unsplash.com/photo-1590196488004-fd25b8532354?auto=format&fit=crop&w=1000&q=80',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Resident in a black sweater hugging his short-coated dog',
    },
  },
};

const image3Def: SlotGroupDefinition = {
  label: 'Pet Image #3',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://images.unsplash.com/photo-1753685724052-8a3215482493?auto=format&fit=crop&w=1000&q=80',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Resident cuddling her dog at home',
    },
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'Pet Policy CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/pet-policy' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Pet Policy' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Component
// ============================================================================

export function PetPolicyCallout() {
  const image1 = useSlotGroup('petpolicycallout.image_1', image1Def);
  const image2 = useSlotGroup('petpolicycallout.image_2', image2Def);
  const image3 = useSlotGroup('petpolicycallout.image_3', image3Def);
  const ctaLink = useSlotGroup('petpolicycallout.cta_link', ctaLinkDef);

  useRevealStyles();
  useSiteCtaStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up bg-[var(--vibe-color-background)] py-12 md:py-16"
    >
      <div className="ppc-grid">
        {/* ── Image 1 ── */}
        <EditableSlotGroup
          groupPath="petpolicycallout.image_1"
          definition={image1Def}
          className="ppc-media"
        >
          {image1.image ? (
            <VibesitesImage
              url={image1.image}
              alt={image1.alt || ''}
              sizes="(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw"
              className="ppc-img"
            />
          ) : (
            <div className="ppc-img ppc-img--empty" />
          )}
        </EditableSlotGroup>

        {/* ── Image 2 ── */}
        <EditableSlotGroup
          groupPath="petpolicycallout.image_2"
          definition={image2Def}
          className="ppc-media"
        >
          {image2.image ? (
            <VibesitesImage
              url={image2.image}
              alt={image2.alt || ''}
              sizes="(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw"
              className="ppc-img"
            />
          ) : (
            <div className="ppc-img ppc-img--empty" />
          )}
        </EditableSlotGroup>

        {/* ── Text block with CTA ── */}
        <div className="ppc-content">
          <RichTextContent
            path="petpolicycallout.heading"
            label="Heading"
            as="h2"
            className="ppc-heading"
          />
          <RichTextContent
            path="petpolicycallout.description"
            label="Description"
            as="div"
            className="ppc-description"
          />
          <EditableSlotGroup
            groupPath="petpolicycallout.cta_link"
            definition={ctaLinkDef}
            className="ppc-cta-wrap"
          >
            <a
              href={ctaLink.url || '/pet-policy'}
              target={ctaLink._target || '_self'}
              className="site-cta site-cta--primary"
            >
              {ctaLink.label || 'Pet Policy'}
            </a>
          </EditableSlotGroup>
        </div>

        {/* ── Image 3 ── */}
        <EditableSlotGroup
          groupPath="petpolicycallout.image_3"
          definition={image3Def}
          className="ppc-media"
        >
          {image3.image ? (
            <VibesitesImage
              url={image3.image}
              alt={image3.alt || ''}
              sizes="(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw"
              className="ppc-img"
            />
          ) : (
            <div className="ppc-img ppc-img--empty" />
          )}
        </EditableSlotGroup>
      </div>

      <style>{`
        .ppc-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1rem;
          width: 100%;
          padding: 0 1rem;
        }
        @media (min-width: 640px) {
          .ppc-grid {
            padding: 0 1.5rem;
          }
        }
        @media (min-width: 640px) {
          .ppc-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }
        @media (min-width: 1024px) {
          .ppc-grid {
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 1.25rem;
          }
        }

        .ppc-media {
          position: relative;
          width: 100%;
          /* Squared on mobile to match .amenities-row-image-slot; taller
             portrait once the grid splits into multiple columns. */
          aspect-ratio: 1 / 1;
          overflow: hidden;
          background: var(--vibe-color-surface);
        }
        @media (min-width: 640px) {
          .ppc-media {
            aspect-ratio: 3 / 4;
          }
        }
        .ppc-img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
        }
        .ppc-img--empty {
          background: var(--vibe-color-surface);
        }

        .ppc-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 2.5rem 1.5rem;
          background: var(--vibe-color-background);
          min-width: 0;
        }
        @media (min-width: 1024px) {
          .ppc-content {
            padding: 2rem 2.5rem;
            aspect-ratio: 3 / 4;
          }
        }

        .ppc-heading {
          font-family: var(--vibe-font-heading);
          font-size: clamp(2rem, 3.2vw, 2.75rem);
          font-weight: 700;
          line-height: 1.05;
          color: var(--vibe-color-primary);
          margin: 0 0 1.5rem;
        }
        .ppc-heading [data-vibesite-richtext] p {
          margin: 0;
        }

        .ppc-description {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.65;
          color: var(--vibe-color-text);
          margin: 0 0 2rem;
          max-width: 22rem;
        }
        .ppc-description [data-vibesite-richtext] p {
          margin: 0 0 1em;
        }
        .ppc-description [data-vibesite-richtext] p:last-child {
          margin-bottom: 0;
        }
        .ppc-description [data-vibesite-richtext] a {
          color: var(--vibe-color-primary);
          text-decoration: underline;
        }

        .ppc-cta-wrap {
          display: inline-block;
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'PetPolicyCallout',
  displayName: 'Pet Policy Callout',
  tokens: {
    'petpolicycallout.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Learn more about our pets policy</p>',
    },
    'petpolicycallout.description': {
      label: 'Description',
      type: 'richtext',
      defaultValue:
        '<p>Home just wouldn&#39;t be the same without your pets, and we invite you to bring them with you to <span data-type="field-token" data-field="property_name"></span>.</p>',
    },
  },
  slotGroups: {
    'petpolicycallout.image_1': image1Def,
    'petpolicycallout.image_2': image2Def,
    'petpolicycallout.image_3': image3Def,
    'petpolicycallout.cta_link': ctaLinkDef,
  },
};

(PetPolicyCallout as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PetPolicyCallout;
